import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import _ from 'lodash';
import memoize from 'memoize-one';
import {v4 as uuidV4} from 'uuid';
import {keys} from 'ts-transformer-keys';
import {enumerate} from 'ts-transformer-enumerate';

import {Button} from 'primereact/button';
import {ContextMenu} from 'primereact/contextmenu';
import {Dialog} from 'primereact/dialog';
import {Toast} from 'primereact/toast';
import TreeNode from 'primereact/treenode/TreeNode';

import StackError from '~/shared/src/base/StackError';
import LocalizableError from '~/shared/src/base/LocalizableError';
import Joi from '~/shared/src/utilities/joiExtended';
import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';
import metaModel from '~/shared/src/utilities/metaModel';
import Item from '~/shared/src/ci/Item';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {
    CheckItemKeyUniquenessParameters,
    CheckItemKeyUniquenessResult,
    SaveDataResult
} from '~/shared/src/utilities/modules/data';
import {ClearConfigParameters} from '~/shared/src/utilities/modules/form';
import {
    BaseRequestParameters,
    defaultBaseRequestParameters
} from '~/shared/src/utilities/modules/common';
import {requestBaseParametersSchema} from '~/shared/src/model/BaseRequest';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {extractDataObjectsFromDataStructure} from '~/shared/src/utilities/dataStructure';

import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import Headerbar from '~/frontend/src/components/base/MapSearch/Headerbar';
import SearchTree, {
    ReferenceType as SearchTreeReferenceType
} from '~/frontend/src/components/base/MapSearch/SearchTree';
import Toolbar from '~/frontend/src/components/base/MapSearch/Toolbar';
import ResizeableLeftMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableLeftMenuPanel';
import ResizeableRightMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableRightMenuPanel';
import Form from '~/frontend/src/components/base/Form';
import MapSearch, {
    ReferenceType as MapSearchReferenceType
} from '~/frontend/src/components/base/MapSearch';
import AttributesForm from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel';
import StringField from '~/frontend/src/components/base/shared/field/valueTypeFields/StringField';
import FieldWrapper from '~/frontend/src/components/base/shared/field/FieldWrapper';
import SchemaDropdown from '~/frontend/src/components/base/shared/field/SchemaDropdown';
import ItemTypeDropdown from '~/frontend/src/components/base/shared/field/ItemTypeDropdown';

import {
    isDataModified,
    isDataModifiedRecursive,
    MergeMode
} from '~/frontend/src/utilities/dataUtils';
import {
    getPanelIcon as getPanelIconOrig,
    getPanelTitle as getPanelTitleOrig,
    setPanelInfo as setPanelInfoOrig
} from '~/frontend/src/utilities/panelInfo';
import {getPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {FormPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/definitions';

import {
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {
    ElusiveDataState,
    PanelElusiveDataState
} from '~/frontend/src/reducers/elusiveData';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import loadData from '~/frontend/src/actions/data/loadData';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import * as TreeActions from '~/frontend/src/actions/panel/treeView';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {
    MsgParams,
    setMessage,
    unsetMessage
} from '~/frontend/src/actions/intercom';
import {
    clearElusiveData,
    clearElusiveDataForSubpanels,
    setElusiveData
} from '~/frontend/src/actions/elusiveData';
import * as CiActions from '~/frontend/src/actions/data';
import setParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import setPanelDirty, {SetPanelDirty} from '~/frontend/src/actions/panelManager/setPanelDirty';
import setPanelInfo, {SetPanelInfo} from '~/frontend/src/actions/panelManager/setPanelInfo';
import closePanel, {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import hideConfirmCloseDialog from '~/frontend/src/actions/panelManager/hideConfirmCloseDialog';
import saveData, {SaveData} from '~/frontend/src/actions/panel/viewDesigner/saveData';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import addItem, {AddItem} from '~/frontend/src/actions/data/addItem';
import addReference, {AddReference} from '~/frontend/src/actions/data/addReference';
import {DeleteReference} from '~/frontend/src/actions/data/deleteReference';
import cancelRequests from '~/frontend/src/actions/ajax/cancelRequests';


const schemata = enumerate<YnSchema>();


type Variables = {
    [ variable : string ] : any;
};


export type DefaultProps = BaseRequestParameters & {
    /**
     * The database schema to filter data and metadata for.
     */
    dataContext? : string;
    //
    title? : string;
    /**
     * The width of the menu on the left side in percent.
     */
    menuWidth? : number;
    /**
     * The width of the menu on the right side in percent.
     */
    menu2Width? : number;
};
export const defaultProps : Required<DefaultProps> = {
    ...defaultBaseRequestParameters,

    schema     : 'DATA',
    dataContext: 'mapSearch',
    title      : 'MapSearchView',
    menuWidth  : 15,
    menu2Width : 20
};

type Props = {
    uid : string;

    // the id of the new or existing MapSearch
    rootItemId : number;

    // the key of the new or existing MapSearch
    rootItemKey : string

    selectedElementId? : number;

    /**
     * The props for sub panels
     */
    subPanelProps? : any;

    onSave? : ( rootItemId : number, dataContext : string ) => void;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type StateProps = {

    /**
     * The full elusiveData
     */
    allElusiveData? : ElusiveDataState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     * A storage for temporary needed data (not persisted to local storage via redux store persist), like
     *  - lastParams: The stringified parameters of the last loaded search. Used to determine if a new result have to be loaded from the backend.
     *  - gridData: The ordered itemIds to display in the grid.
     *  - totalRecords: The total result data count to be displayed in the DataTable.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;
};

const Actions = Object.assign( { loadData, setMessage, unsetMessage, setElusiveData, clearElusiveData, clearElusiveDataForSubpanels, setPanelInfo, setPanelDirty, addItem, addReference, closePanel }, PanelActions, TreeActions, IntercomActions, CiActions );

type SetParam = <K extends keyof ComponentProps>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof ComponentProps ) => UnsetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<ComponentProps> ) => SetParamsAction;

/**
 * Function props mapped by mapDispatchToProps
 */
type ActionPropsRaw = {
    //updatePanelProps: typeof updatePanelProps;
    loadData : typeof loadData;
    setParam : SetParam;
    setParams : SetParams;
    unsetParam : UnsetParam;
    setMessage : typeof setMessage;
    unsetMessage : typeof unsetMessage;
    setPanelInfo : SetPanelInfo;
    setElusiveData : typeof setElusiveData;
    clearElusiveData : typeof clearElusiveData;
    clearElusiveDataForSubpanels : typeof clearElusiveDataForSubpanels;
    setPanelDirty : SetPanelDirty;
    deleteReference : DeleteReference;
    addItem : AddItem;
    addReference : AddReference;

};

type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
} & {
    closePanel : ClosePanel;
};

type AllProps = Required<DefaultProps> & Props & StateProps & ActionProps;


type PanelElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    showCouldNotSavePopup? : boolean
};


type State = {
    isDirty : boolean;
    errorCount : number;
    errorTooltip? : string | React.ReactNode | ( string | React.ReactNode )[];
    errorMessage? : string | React.ReactNode | ( string | React.ReactNode )[];
    rootItemFormState : 'CLEAR' | 'DIRTY' | 'ERROR';

    initialized : boolean;

    mapSearchItemTypeId : number;

    showCreateNewMapSearchDialog : boolean;

    createNewMapSearchDialogKey? : string;
    createNewMapSearchDialogKeyError? : string;
    createNewMapSearchDialogSchema : Schema;
    createNewMapSearchDialogItemType? : string;

    /**
     * Counts up on menu scroll. Used to fire update for arrows
     */
    scrollIndicator : number;

    mobile : boolean;

    lastCheckedKey? : string;
};


const getMobile = () => matchMedia( 'screen and (max-width: 960px)' ).matches ? true : false;


/**
 * Playground to test Tree implementation
 */
class MapSearchView extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    // panel manager config
    static panelType = 'MapSearchView';
    static panelTabTitle = 'YN.base.frontend.mapSearch.title';
    static panelTabIcon = 'itemTypeIcon:CFG:MapSearch';

    static panelPropsSchemaMsgIdPrefix = 'YN.base.frontend.mapSearch';
    static panelPropsSchema = Joi.object()
        // panelSchema defaults
        .concat( requestBaseParametersSchema )
        .concat( Joi.object( {
            panelType    : Joi.string().valid( MapSearchView.panelType ),
            title        : Joi.string(),
            panelTabTitle: Joi.string().default( MapSearchView.panelTabTitle ),
            panelTabIcon : Joi.string().default( MapSearchView.panelTabIcon ),
            rootItemId   : Joi.number().description( 'Id of the MapSearch.' ),
            rootItemKey  : Joi.string().description( 'Key of MapSearch' ),
            menuWidth    : Joi.number()
                .default( 15 )
                .description( 'The width of the left menu panel in percent of the available width.' ),
            menu2Width   : Joi.number()
                .default( 30 )
                .description( 'The width of the right menu panel in percent of the available width.' ),
            onSave       : Joi.function()
        } ) );

    /**
     * YOONITE lifecycle method
     */
    static onClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const xhrState      = getState()?.xhr,
                  componentUids = xhrState?.components
                      ? Object.keys( xhrState.components ).reduce(
                          ( acc, uid ) => {
                              if ( String( uid ).indexOf( props.uid ) === 0 ) {
                                  acc.push( String( uid ) );
                              }
                              return acc;
                          },
                          [] as string[]
                      )
                      : [],
                  dataContexts  = ( Object.keys( global.data ) as string[] ).reduce(
                      ( acc, dC ) => {
                          if ( dC.indexOf( props.uid ) === 0 || ( props.dataContext && dC.indexOf( props.dataContext ) === 0 ) ) {
                              acc.push( dC );
                          }
                          return acc;
                      },
                      [] as string[]
                  );

            // abort running requests
            for ( const uid of componentUids ) {
                dispatch( cancelRequests( uid ) );
            }

            // clear global data
            for ( const dataContext of dataContexts ) {
                delete global.data[ dataContext ];
            }

            // clear backend data of all loaded forms
            for ( const subPanelProps of Object.values( props.subPanelProps ?? {} ) as any[] ) {
                if ( subPanelProps.moduleTransformer === 'form' && subPanelProps.configHash ) {
                    const params : ClearConfigParameters = {
                        configHash: subPanelProps.configHash
                    };

                    await xhr.post( `${BASE_URL}/api/form/clearConfig`, params, undefined, props.uid, props.uid, dispatch );
                }
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            await MapSearchView.savePanelData(
                dispatch,
                getState,
                props
            );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const result = await MapSearchView.savePanelData(
                dispatch,
                getState,
                props,
                [
                    props.uid,
                    'notification',
                    'saveSuccess',
                    'closePanel'
                ]
            );

            // no save error -> close panel
            if ( result !== false && result.success ) {
                await dispatch( closePanel( props.uid ) );
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : Pick<AllProps, 'dataContext' | 'rootItemId' | 'rootItemKey'> ) : string | undefined {
        const {
                  dataContext,
                  rootItemId,
                  rootItemKey
              }        = props,
              itemId   = rootItemId
                  ?? dataUtils.findItem( dataContext, 'MapSearch', rootItemKey ?? '' )?.id
                  ?? 0,
              rootItem = global.data[ dataContext ]?.items?.[ itemId ],
              retVal   = rootItem ? `#${MapSearchView.panelType}/${encodeURIComponent( rootItem.key )}` : undefined;

        return retVal;
    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {},
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        // params[ 0 ] = panelType

        // params[ 1 ] = rootItem.key
        if ( params?.[ 1 ] ) {
            const viewKey = decodeURIComponent( params[ 1 ] );

            retVal.rootItemKey = viewKey;
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.frontend.mapSearch.resolvePanelPropsFromURL.error.couldNotResolveViewKey' ) );
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        return retVal;
    }

    static getDataDataContext( dataContext : string ) {
        return `${dataContext}_data`;
    }

    static async doSaveData( uid : string, dispatch : AppDispatch, successMsgParams : MsgParams | undefined = undefined ) : ReturnType<ReturnType<SaveData>> {
        const props                                              = getPanelProps( uid )?.panelProps as ComponentProps,
              propsWithDefaults : Required<DefaultProps> & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  dataContext,
                  rootItemId,
                  selectedElementId,
                  onSave
              }                                                  = propsWithDefaults,
              rootItemForSave                                    = global.data[ uid ].items[ rootItemId! ]!,
              result                                             = await dispatch( saveData( uid, 'CFG', rootItemForSave.id, undefined, undefined, undefined, successMsgParams ) );


        // TODO: if rootItemForSave.isNew()  show popup for entering name


        // remap selectedLayoutElement id
        if ( selectedElementId && result && result.data.itemMetaDataMapping[ String( selectedElementId ) as unknown as number ] ) {
            dispatch( setParam( uid, 'selectedElementId', result.data.itemMetaDataMapping[ String( selectedElementId ) as unknown as number ].id ) );
        }

        if ( onSave ) {
            // remap rootItem id
            if ( result && result.data.itemMetaDataMapping[ String( rootItemForSave.id ) as unknown as number ] ) {
                onSave( result.data.itemMetaDataMapping[ String( rootItemForSave.id ) as unknown as number ].id, dataContext );
            }
            else {
                onSave( rootItemForSave.id, dataContext );
            }
        }

        return result;
    }

    static async savePanelData( dispatch : AppDispatch, getState : () => StoreState, props : ComponentProps, successMsgParams : MsgParams | undefined = undefined ) : Promise<SaveDataResult | false> {
        const propsWithDefaults : DefaultProps & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  uid
              }                                        = propsWithDefaults,
              allElusiveData                           = getState()?.elusiveData;

        let retVal : Promise<SaveDataResult | false> = Promise.resolve( false ),
            hasError : boolean                       = false;

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData;

                    if ( panelElusiveData.errorData?.count ) {
                        hasError = true;
                        break;
                    }
                }
            }
        }

        // panel has errors -> show popup
        if ( hasError ) {
            dispatch( hideConfirmCloseDialog( uid ) );
            dispatch( setElusiveData( uid, { showCouldNotSavePopup: true } ) );
        }

        // panel has no errors -> save
        else {
            retVal = MapSearchView.doSaveData( uid, dispatch, successMsgParams );
        }

        return retVal;
    }


    getDataDataContext : typeof MapSearchView.getDataDataContext;
    menuContentPanelContentStyle = { padding: 0 };

    isLoading : boolean = false;

    cm : InstanceType<typeof ContextMenu> | null = null;
    toast : Toast | null = null;
    tree : SearchTreeReferenceType | null = null;
    mapSearch : MapSearchReferenceType | null = null;

    getTreeContextMenu = ( treeData : TreeNode.TreeNode[], key : string ) => [
        {
            label  : 'Collapse all',
            icon   : 'far fa-minus-square',
            command: function() {
                this.setState( { [ key ]: {} } );
            }.bind( this )
        },
        {
            label  : 'Expand all',
            icon   : 'far fa-plus-square',
            command: function() {
                const expandedKeys : {
                    [ key : string ] : true
                } = {};
                const expandNode = ( node : TreeNode.TreeNode ) => {
                    if ( node.children?.length ) {
                        expandedKeys[ node.key! ] = true;

                        for ( const child of node.children ) {
                            expandNode( child );
                        }
                    }
                }

                for ( const node of treeData ) {
                    expandNode( node );
                }

                this.setState( { [ key ]: expandedKeys } );
            }.bind( this )
        }
    ];

    getPanelIcon = getPanelIconOrig;
    getPanelTitle = getPanelTitleOrig;
    setPanelInfo = setPanelInfoOrig;

    /**
     * React Component constructor
     * @param {Object} props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            isDirty          : false,
            errorCount       : 0,
            rootItemFormState: 'CLEAR',

            scrollIndicator: 0,

            initialized: false,

            mapSearchItemTypeId: metaModel[ 'CFG' ].getItemType( 'MapSearch' )?.id ?? 0,

            showCreateNewMapSearchDialog: false,

            createNewMapSearchDialogKey     : props.rootItemKey ? props.rootItemKey : undefined,
            createNewMapSearchDialogSchema  : 'DATA',
            createNewMapSearchDialogItemType: undefined,

            mobile: getMobile()
        };

        this.checkForCreateNewMapSearchPrerequisits = this.checkForCreateNewMapSearchPrerequisits.bind( this );
        this.onCreateNewMapSearchDialogHide = this.onCreateNewMapSearchDialogHide.bind( this );
        this.onCreateNewMapSearchDialogOkButtonClick = this.onCreateNewMapSearchDialogOkButtonClick.bind( this );
        this.onCreateNewMapSearchDialogSetItemType = this.onCreateNewMapSearchDialogSetItemType.bind( this );
        this.onCreateNewMapSearchDialogSetKey = this.onCreateNewMapSearchDialogSetKey.bind( this );
        this.onCreateNewMapSearchDialogSetSchema = this.onCreateNewMapSearchDialogSetSchema.bind( this );
        this.getDataDataContext = memoize( MapSearchView.getDataDataContext );
        this.checkForIntercomMessages = memoize( this.checkForIntercomMessages );
        this.getSearchTree = memoize( this.getSearchTree, this.getSearchTreeEqual );
        this.getForm = memoize( this.getForm, this.getFormEqual );
        this.getMapSearch = memoize( this.getMapSearch, this.getMapSearchEqual );
        this.getHeaderbar = memoize( this.getHeaderbar );
        this.getPanelIcon = memoize( this.getPanelIcon );
        this.getPanelTitle = memoize( this.getPanelTitle );
        this.getToolbar = memoize( this.getToolbar );
        this.setPanelInfo = memoize( this.setPanelInfo );

        this.checkKeyUniqueness = _.throttle( this.checkKeyUniqueness, 200, { trailing: true } );
        this.onMenuScroll = _.throttle( this.onMenuScroll, 100, { trailing: true } );

        this.onMatchMobileChangeHandler = this.onMatchMobileChangeHandler.bind( this );
        this.onMenu2WidthChange = this.onMenu2WidthChange.bind( this );
        this.onMenuWidthChange = this.onMenuWidthChange.bind( this );
        this.onReloadButtonClick = this.onReloadButtonClick.bind( this );
        this.onSelectElement = this.onSelectElement.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  uid,
                  dataContext,
                  subPanelProps,
                  rootItemId,
                  allElusiveData
              }                                               = props,
              tooltipErrors : ( string | React.ReactNode )[ ] = [],
              messageErrors : ( string | React.ReactNode )[ ] = [];

        let isDirty : boolean                              = rootItemId ? isDataModifiedRecursive( dataContext, 'CFG', rootItemId ) : isDataModified( dataContext, 'CFG' ),
            errorCount                                     = 0,
            rootItemFormState : State['rootItemFormState'] = 'CLEAR';

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData,
                          panelKey         = elusiveDataPanelKey.replace( `${uid}_`, '' );

                    // only check direct sub forms and trees
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                    if ( ( panelKey.match( /^form-/ ) || panelKey.match( /^tree-/ ) ) && panelKey.indexOf( '_' ) === -1 ) {
                        const panelProps   = subPanelProps?.[ panelKey ],
                              isForm       = !!panelKey.match( /^form-/ ),
                              formRootItem = panelProps
                                  ? global.data[ dataContext ]?.items[ panelProps.rootItemId ]
                                  : undefined,
                              elementName  = formRootItem
                                  ? formRootItem.name
                                      ? formRootItem.name
                                      : formRootItem?.key
                                  : '',
                              elementType  = formRootItem ? formRootItem.itemType.label : 'Element';

                        isDirty = isDirty || !!panelElusiveData.isDirty;

                        if ( formRootItem?.id === rootItemId ) {
                            if ( panelElusiveData.errorData?.count ) {
                                rootItemFormState = 'ERROR';
                            }
                            else if ( panelElusiveData.isDirty ) {
                                rootItemFormState = 'DIRTY';
                            }
                        }

                        if ( panelElusiveData.errorData?.count ) {
                            errorCount += panelElusiveData.errorData.count;

                            if ( panelElusiveData.errorData.tooltip?.length ) {
                                tooltipErrors.push(
                                    <>
                                        {isForm &&
                                            <div className="yn-tooltip-header2">
                                                {__( `${MapSearchView.panelPropsSchemaMsgIdPrefix}.error.tooltip.title`, { type: elementType, name: elementName } )}
                                            </div>
                                        }
                                        {panelElusiveData.errorData.tooltip}
                                    </>
                                );
                            }

                            if ( panelElusiveData.errorData.message?.length ) {
                                if ( isForm ) {
                                    messageErrors.push(
                                        <>
                                            <div className="yn-error-subtitle">
                                                {__( `${MapSearchView.panelPropsSchemaMsgIdPrefix}.error.message.title`, { type: elementType, name: elementName } )}
                                            </div>
                                            <hr className="yn-error-ruler"/>
                                            <div className="yn-error-suberror">
                                                {panelElusiveData.errorData.message}
                                            </div>
                                        </>
                                    );
                                }
                                else {
                                    messageErrors.push( panelElusiveData.errorData.message );
                                }
                            }
                        }
                    }
                }
            }
        }

        return {
            isDirty,
            errorCount,
            errorTooltip: errorCount
                ? <div key="error" className="yn-tooltip-error">
                    <div className="yn-tooltip-header">
                        {__( 'YN.base.frontend.dataTable.tooltip.error.title' )}
                    </div>
                    {tooltipErrors}
                </div>
                : undefined,
            errorMessage: errorCount
                ? <>
                    <div className="yn-error-message">
                        {__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.text' )}
                    </div>
                    <hr className="yn-error-ruler"/>
                    <div className="yn-error-suberror">
                        {messageErrors}
                    </div>
                </>
                : undefined,
            rootItemFormState
        };
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        matchMedia( 'screen and (max-width: 960px)' ).addEventListener( 'change', this.onMatchMobileChangeHandler );

        this.afterRender();

        if ( !this.props.elusiveData ) {
            if ( this.props.rootItemId || this.props.rootItemKey ) {
                void this.loadData();
            }
            else {
                this.checkForCreateNewMapSearchPrerequisits();
            }
        }
        else {
            this.setState( {
                initialized: true
            } );
        }
    }

    /**
     * REACT lifecycle method
     */
    componentWillUnmount() {
        matchMedia( 'screen and (max-width: 960px)' ).removeEventListener( 'change', this.onMatchMobileChangeHandler );
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : Readonly<AllProps>, prevState : Readonly<State> ) {
        this.afterRender();

        // key changed or dialog shown
        if (
            prevState.createNewMapSearchDialogKey !== this.state.createNewMapSearchDialogKey
            || ( !prevState.showCreateNewMapSearchDialog && this.state.showCreateNewMapSearchDialog )
        ) {
            this.setState( { createNewMapSearchDialogKeyError: __( 'YN.base.finder.item.key.check.unique' ) } );
            this.checkKeyUniqueness();
        }
    }

    /**
     * REACT lifecycle method
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        if (
            ( nextState.isDirty !== undefined && nextState.isDirty !== this.state.isDirty )
        ) {
            this.props.setPanelDirty( this.props.uid, nextState.isDirty );
        }

        return !this.isLoading;
    }

    async loadData() : Promise<boolean> {
        this.isLoading = true;

        const {
                  dataContext,
                  rootItemId,
                  rootItemKey,
                  uid
              }      = this.props,
              retVal = await this.props.loadData?.(
                  'map/loadMapSearchConfigData',
                  uid,
                  dataContext,
                  undefined,
                  'CFG',
                  {
                      params   : {
                          viewId : rootItemId,
                          viewKey: rootItemId ? undefined : rootItemKey
                      },
                      mergeMode: 'keepExisting' as MergeMode
                  },
                  undefined,
                  resonseData => {
                      if ( resonseData?.data.rootItemId && rootItemId !== resonseData.data.rootItemId ) {
                          this.props.setParam( uid, 'rootItemId', resonseData.data.rootItemId );
                      }

                      if ( !resonseData?.data.items.length ) {
                          this.checkForCreateNewMapSearchPrerequisits();
                      }
                      else {
                          let itemId : number | undefined = rootItemId,
                              rootItem : Item | undefined;

                          if ( resonseData?.data.rootItemId && rootItemId !== resonseData.data.rootItemId ) {
                              itemId = resonseData.data.rootItemId;
                          }

                          if ( itemId ) {
                              rootItem = global.data[ dataContext ]?.items[ itemId ];
                          }
                          else if ( rootItemKey ) {
                              rootItem = dataUtils.findItem( dataContext, 'MapSearch', rootItemKey );
                          }

                          if ( rootItem ) {
                              this.props.setParam( uid, 'rootItemId', rootItem.id );
                              this.props.setParam( uid, 'rootItemKey', rootItem.key );
                          }

                          this.setState( {
                              initialized: true
                          } );
                      }
                  }
              );

        this.isLoading = false;

        return retVal;
    }

    afterRender( props : AllProps = this.props as AllProps ) {
        const {
                  dataContext,
                  intercomMessages,
                  rootItemId,
                  rootItemKey,
                  uid
              }     = props,
              {
                  isDirty,
                  errorCount
              }     = this.state,
              icon  = this.getPanelIcon( MapSearchView.panelTabIcon ),
              title = this.getPanelTitle( MapSearchView.panelTabTitle, props.title ),
              path  = MapSearchView.buildPanelPathFromProps( { dataContext, rootItemId, rootItemKey } );

        this.checkForIntercomMessages( intercomMessages );

        this.setPanelInfo( uid, icon, title, undefined, isDirty, !!errorCount, path );
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages ? : PanelIntercomState ) {
        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );
            for ( const key of toastKeys ) {
                // show toast
                if ( intercomMessages.toast[ key ] ) {
                    this.toast.show( intercomMessages.toast[ key ] );
                }

                // clear toast message
                this.props.unsetMessage?.( this.props.uid, 'toast', key as string );
            }
        }
    }

    checkForCreateNewMapSearchPrerequisits() {
        const {
                  createNewMapSearchDialogItemType
              } = this.state;

        // no root DataObject ItemType -> show dialog
        if ( !createNewMapSearchDialogItemType ) {
            this.setState( {
                initialized                 : true,
                showCreateNewMapSearchDialog: true
            } );
        }
        else {
            this.setState( {
                initialized: true
            } );

            this.createNewMapSearch();
        }
    }

    /**
     * Checks the given key for uniqueness within the given ItemType against the backend.
     *
     * @private
     */
    checkKeyUniqueness() {
        const {
                  uid
              }             = this.props, {
                  mapSearchItemTypeId,
                  createNewMapSearchDialogKey
              }             = this.state,
              compopnentUid = `${uid}_check`,
              requestUid    = 'checkKeyUniqueness';

        if ( mapSearchItemTypeId && createNewMapSearchDialogKey ) {

            // abort running check
            if ( this.props.xhrRequestsForComponent?.[ requestUid ] ) {
                xhr.abort( compopnentUid, requestUid );
            }

            const doLoad = async () => {
                const params : CheckItemKeyUniquenessParameters = {
                          schema  : 'CFG',
                          status  : 'all',
                          itemType: mapSearchItemTypeId,
                          key     : createNewMapSearchDialogKey
                      },
                      response                                  = await xhr.post(
                          `${BASE_URL}/api/data/checkItemKeyUniqueness`,
                          params,
                          undefined,
                          compopnentUid,
                          requestUid
                      );

                if ( response?.data ) {
                    const responseData = response.data as CheckItemKeyUniquenessResult;

                    if ( responseData.success && !responseData.data.keyIsUnique ) {
                        this.setState( { createNewMapSearchDialogKeyError: __( 'YN.base.frontend.mapSearch.createNewDialog.key.error.notUnique' ) } );
                    }
                    else {
                        this.setState( { createNewMapSearchDialogKeyError: '' } );
                    }
                }
            };

            doLoad().catch( ( error ) => {
                console.log( error );
            } );
        }
    }

    createNewMapSearch() {
        const {
                  uid,
                  dataContext
              }                                     = this.props,
              {
                  mapSearchItemTypeId,
                  createNewMapSearchDialogKey,
                  createNewMapSearchDialogSchema,
                  createNewMapSearchDialogItemType
              }                                     = this.state,
              activeStatusId                        = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
              mapDataSetItemTypeId                  = metaModel[ 'CFG' ].getItemType( 'MapDataSet' )!.id,
              mDSOrderPropertySetId                 = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( mapDataSetItemTypeId, 'Order' )!.id,
              mDSSearchGroupPropertySetId           = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( mapDataSetItemTypeId, 'SearchGroup' )!.id,
              mDSGetsDataForMapSearchReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( mapDataSetItemTypeId, 'MapSearch', 'getsDataFor', 'src' )![ 0 ].id,
              queryItemTypeId                       = metaModel[ 'CFG' ].getItemType( 'Query' )!.id,
              qSchemaPropertySetId                  = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( queryItemTypeId, 'Schema' )!.id,
              qTypePropertySetId                    = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( queryItemTypeId, 'Type' )!.id,
              qGetsDataForMDSReferenceSetId         = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( queryItemTypeId, mapDataSetItemTypeId, 'getsDataFor', 'src' )![ 0 ].id,
              dataObjectItemTypeId                  = metaModel[ 'CFG' ].getItemType( 'DataObject' )!.id,
              dOObjectSchemaPropertySetId           = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectSchema' )!.id,
              dOObjectTypePropertySetId             = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectType' )!.id,
              dOIsRootOfQReferenceSetId             = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( dataObjectItemTypeId, queryItemTypeId, 'isRootOf', 'src' )![ 0 ].id;

        if ( createNewMapSearchDialogKey && createNewMapSearchDialogSchema && createNewMapSearchDialogItemType ) {
            const mapSearch      = this.props.addItem( dataContext, {
                      itemTypeId: mapSearchItemTypeId,
                      key       : createNewMapSearchDialogKey,
                      name      : `${createNewMapSearchDialogKey} - ${createNewMapSearchDialogItemType}`,
                      statusId  : activeStatusId
                  }, 'CFG' ),
                  mapDataSet     = this.props.addItem(
                      dataContext,
                      {
                          statusId  : activeStatusId,
                          itemTypeId: mapDataSetItemTypeId,
                          key       : `${createNewMapSearchDialogKey}:${uuidV4().substring( 0, 8 )}`,
                          name      : createNewMapSearchDialogKey,
                          properties: [
                              {
                                  propertySetId: mDSOrderPropertySetId,
                                  value        : 1,
                                  statusId     : activeStatusId
                              },
                              {
                                  propertySetId: mDSSearchGroupPropertySetId,
                                  value        : createNewMapSearchDialogKey,
                                  statusId     : activeStatusId
                              }
                          ]
                      },
                      'CFG'
                  ),
                  query          = this.props.addItem(
                      dataContext,
                      {
                          statusId  : activeStatusId,
                          itemTypeId: queryItemTypeId,
                          key       : `${createNewMapSearchDialogKey}:${uuidV4().substring( 0, 8 )}`,
                          name      : createNewMapSearchDialogKey,
                          properties: [
                              {
                                  propertySetId: qSchemaPropertySetId,
                                  value        : createNewMapSearchDialogSchema.toLowerCase(),
                                  statusId     : activeStatusId
                              },
                              {
                                  propertySetId: qTypePropertySetId,
                                  value        : 'DataStructure',
                                  statusId     : activeStatusId
                              }
                          ]
                      },
                      'CFG'
                  ),
                  rootDataObject = this.props.addItem(
                      dataContext,
                      {
                          statusId  : activeStatusId,
                          itemTypeId: dataObjectItemTypeId,
                          key       : `${createNewMapSearchDialogKey}:${createNewMapSearchDialogItemType}:${uuidV4().substring( 0, 8 )}`,
                          name      : createNewMapSearchDialogItemType,
                          properties: [
                              {
                                  propertySetId: dOObjectSchemaPropertySetId,
                                  value        : createNewMapSearchDialogSchema.toLowerCase(),
                                  statusId     : activeStatusId
                              },
                              {
                                  propertySetId: dOObjectTypePropertySetId,
                                  value        : createNewMapSearchDialogItemType,
                                  statusId     : activeStatusId
                              }
                          ]
                      },
                      'CFG'
                  );

            if ( mapSearch && mapDataSet && query && rootDataObject ) {

                // reference new MapDataSet to new MapSearch
                this.props.addReference(
                    dataContext,
                    {
                        referenceSetId: mDSGetsDataForMapSearchReferenceSetId,
                        statusId      : activeStatusId,
                        srcItemId     : mapDataSet.id,
                        destItemId    : mapSearch.id
                    },
                    'CFG'
                );

                // reference new Query to new MapDataSet
                this.props.addReference(
                    dataContext,
                    {
                        referenceSetId: qGetsDataForMDSReferenceSetId,
                        statusId      : activeStatusId,
                        srcItemId     : query.id,
                        destItemId    : mapDataSet.id
                    },
                    'CFG'
                );

                // reference new root DataObject to new Query
                this.props.addReference(
                    dataContext,
                    {
                        referenceSetId: dOIsRootOfQReferenceSetId,
                        statusId      : activeStatusId,
                        srcItemId     : rootDataObject.id,
                        destItemId    : query.id
                    },
                    'CFG'
                );

                this.props.setParam( uid, 'rootItemId', mapSearch.id );
                this.props.setParam( uid, 'rootItemKey', mapSearch.key );
                this.setState( {
                    initialized: true
                } );
            }
        }
    }

    onReloadButtonClick() {
        const {
                  uid,
                  dataContext
              }            = this.props,
              dataContexts = ( Object.keys( global.data ) as string[] ).reduce(
                  ( acc, dCKey ) => {
                      if ( dCKey === uid || dCKey === dataContext || dCKey.indexOf( `${dataContext}_mapDataSet_` ) === 0 ) {
                          acc.push( dCKey );
                      }
                      return acc;
                  },
                  [] as string[]
              );

        if ( dataContexts.length ) {
            this.isLoading = true;

            this.props.clearElusiveData?.( uid );
            this.props.clearElusiveDataForSubpanels?.( uid );

            // clean global data
            for ( const dC of dataContexts ) {
                global.data[ dC ] = {
                    items     : [],
                    references: [],
                    schema    : global.data[ dC ].schema
                };
            }
            this.mapSearch?.resetForReload();

            window.setTimeout(
                () => {
                    void this.loadData();
                },
                1
            );
        }
    }

    onMenuScroll() {
        this.setState( { scrollIndicator: this.state.scrollIndicator! + 1 } )
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenuWidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menuWidth', width );
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenu2WidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menu2Width', width );
    }

    onSelectElement( selectedElementId ? : number ) {
        this.props.setParam?.( this.props.uid, 'selectedElementId', selectedElementId );
    }

    onCreateNewMapSearchDialogOkButtonClick() {
        this.createNewMapSearch();
        this.setState( { showCreateNewMapSearchDialog: false } );
    }

    onCreateNewMapSearchDialogHide() {
        this.props.closePanel( this.props.uid );
    }

    onCreateNewMapSearchDialogSetKey( value : string | undefined ) {
        let key      = '',
            keyError = '';

        if ( value !== undefined ) {
            key = value;

            if ( !key ) {
                keyError = __( 'YN.base.finder.item.key.error.empty' );
            }
            else if ( / /.test( key ) ) {
                keyError = __( 'YN.base.finder.item.key.error.whitespaces' );
            }

            this.setState( {
                createNewMapSearchDialogKey     : key,
                createNewMapSearchDialogKeyError: keyError
            } );
        }
    }

    onCreateNewMapSearchDialogSetSchema( value : string ) {
        const {
                  createNewMapSearchDialogItemType,
                  createNewMapSearchDialogSchema
              }               = this.state,
              oldGeneratedKey = createNewMapSearchDialogSchema === 'CFG'
                  ? `CFG:${createNewMapSearchDialogItemType ?? ''}`
                  : createNewMapSearchDialogItemType ?? '';

        let {
                createNewMapSearchDialogKey,
                createNewMapSearchDialogKeyError
            } = this.state;

        if ( !createNewMapSearchDialogKey || createNewMapSearchDialogKey === oldGeneratedKey ) {
            createNewMapSearchDialogKey = value === 'CFG' ? 'CFG:' : '';
            createNewMapSearchDialogKeyError = undefined;
        }

        this.setState( {
            createNewMapSearchDialogSchema  : value as Schema,
            createNewMapSearchDialogItemType: undefined,
            createNewMapSearchDialogKey,
            createNewMapSearchDialogKeyError
        } );
    }

    onCreateNewMapSearchDialogSetItemType( value : string ) {
        const {
                  createNewMapSearchDialogItemType,
                  createNewMapSearchDialogSchema
              }               = this.state,
              oldGeneratedKey = createNewMapSearchDialogSchema === 'CFG'
                  ? `CFG:${createNewMapSearchDialogItemType ?? ''}`
                  : createNewMapSearchDialogItemType ?? '';

        let {
                createNewMapSearchDialogKey,
                createNewMapSearchDialogKeyError
            } = this.state;

        if ( !createNewMapSearchDialogKey || createNewMapSearchDialogKey === oldGeneratedKey ) {
            createNewMapSearchDialogKey = createNewMapSearchDialogSchema === 'CFG' ? 'CFG:' + value : value;
            createNewMapSearchDialogKeyError = undefined;
        }

        this.setState( {
            createNewMapSearchDialogItemType: value,
            createNewMapSearchDialogKey,
            createNewMapSearchDialogKeyError
        } );
    }

    onMatchMobileChangeHandler() {
        this.setState( { mobile: getMobile() } );
    }

    getSelectableSchemata( user? : User ) {
        const retVal : Schema[] = [];

        for ( const schema of schemata ) {
            const itemTypes = metaModel[ schema ].getItemTypes() ?? [];

            for ( const itemType of itemTypes ) {
                if (
                    itemType.id > 0
                    && user?.hasGenericPermissionOnItemType( 'read', itemType.id, schema )
                ) {
                    retVal.push( schema );
                    break;
                }
            }
        }

        retVal.sort();

        return retVal;
    }

    getCreateNewMapSearchDialog() {
        const {
                  uid,
                  user
              }                           = this.props,
              {
                  showCreateNewMapSearchDialog,
                  createNewMapSearchDialogKey,
                  createNewMapSearchDialogKeyError,
                  createNewMapSearchDialogSchema,
                  createNewMapSearchDialogItemType
              }                           = this.state,
              okDisabled                  = Boolean( !createNewMapSearchDialogKey || !createNewMapSearchDialogItemType ),
              selectableSchemata          = this.getSelectableSchemata( user ),
              style : React.CSSProperties = {
                  width           : '33rem',
                  minWidth        : '33rem',
                  [ '--min-width' ]: '33rem',
                  minHeight       : '25vh'
              };

        // only one selectable schemata and not set -> set it
        if ( selectableSchemata.length === 1 && createNewMapSearchDialogSchema !== selectableSchemata[ 0 ] ) {
            this.setState( { createNewMapSearchDialogSchema: selectableSchemata[ 0 ] } );
            return;
        }

        return <Dialog
            key={`${uid}_createNewMapSearchDialog`}
            id={`${uid}_createNewMapSearchDialog`}

            visible={showCreateNewMapSearchDialog}
            onHide={this.onCreateNewMapSearchDialogHide}
            focusOnShow={false}
            closeOnEscape={false}
            className="yn-form-dialog"
            style={style}
            appendTo={( document.querySelector( '.yn-tabpanel .p-tabview-panel' ) ?? undefined ) as HTMLElement | undefined}
            header={__( 'YN.base.frontend.mapSearch.createNewDialog.title' )}
            footer={<div>
                <Button
                    id={`${uid}_okButton`}
                    key="okButton"
                    label={__( 'YN.base.common.ok' )}
                    className="p-button-outlined mr-3"
                    disabled={okDisabled}
                    onClick={this.onCreateNewMapSearchDialogOkButtonClick}
                />
                <Button
                    id={`${uid}_cancelButton`}
                    key="cancelButton"
                    label={__( 'YN.base.common.cancel' )}
                    className="p-button-secondary p-button-outlined"
                    onClick={this.onCreateNewMapSearchDialogHide}
                />
            </div>}
        >
            <StringField
                uid={`${uid}_keyField`}
                label={__( 'YN.base.frontend.mapSearch.createNewDialog.key.label' )}
                value={createNewMapSearchDialogKey}
                errorMessage={createNewMapSearchDialogKeyError}
                style={{ width: '30rem' }}
                labelPosition="top"
                infoTextLayout="hidden"
                autoComplete={false}
                markDirty={false}
                onChange={this.onCreateNewMapSearchDialogSetKey}
            />
            <FieldWrapper
                uid={`${uid}_itemTypeField`}
                label={__( 'YN.base.frontend.mapSearch.createNewDialog.itemType.label' )}
                labelPosition="top"
                infoTextLayout="hidden"
                isDirty={false}
                autoComplete={false}
            >
                {selectableSchemata.length > 1 &&
                    <SchemaDropdown
                        uid={uid}
                        value={createNewMapSearchDialogSchema}
                        onChange={this.onCreateNewMapSearchDialogSetSchema}

                        style={{ flex: '0 0 7rem', width: '7rem', paddingRight: '.5rem' }}
                    />
                }
                <ItemTypeDropdown
                    uid={uid}

                    schema={createNewMapSearchDialogSchema}
                    value={createNewMapSearchDialogItemType}
                    rawMode={false}
                    showAdditionalRawNames={false}
                    markDirty={false}
                    onChange={this.onCreateNewMapSearchDialogSetItemType}
                    inputStyle={{ width: selectableSchemata.length > 1 ? '23rem' : '30rem' }}
                />
            </FieldWrapper>
        </Dialog>;
    }

    getHeaderbar( uid : string, dataContext : string, viewItem? : Item ) {
        return <Headerbar
            key={`${uid}_headerbar`}

            uid={uid}

            dataContext={dataContext}
            rootItemId={viewItem?.id}
        />;
    }

    getToolbar( uid : string, dataContext : string, viewItem : Item | undefined, readOnly : boolean, saveButtonErrorCount : number, saveButtonErrorTooltip : React.ReactNode | React.ReactNode[] ) {
        return <Toolbar
            key={`${uid}_MapSearchToolbar`}

            uid={uid}

            dataContext={dataContext}
            rootItemId={viewItem?.id}

            saveButtonDisabled={readOnly}
            saveButtonErrorCount={saveButtonErrorCount}
            saveButtonErrorTooltip={saveButtonErrorTooltip}
            deleteButtonDisabled={readOnly}

            onReloadButtonClick={this.onReloadButtonClick}
        />;
    }

    getSearchTree( uid : string, dataContext : string, searchTreeProps : any, viewItem : Item | undefined, readOnly : boolean, additionalElusiveDataContexts : string[], selectedElementId? : number ) {
        return <SearchTree
            key={uid}

            ref={( el ) => this.tree = el}

            {...searchTreeProps}

            uid={uid}

            readOnly={readOnly}

            dataContext={dataContext}
            rootItemId={viewItem?.id}

            additionalElusiveDataContexts={additionalElusiveDataContexts}

            selectedElementId={selectedElementId}
            onSelectElement={this.onSelectElement}

            className="yn-sidebar-content p-tree"
        />;
    }

    getSearchTreeEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getMapSearch( uid : string, dataContext : string, dataDataContext : string, rootItemFormState : State['rootItemFormState'], viewItem? : Item, selectedElementId? : number ) {
        return <MapSearch
            key={uid}

            ref={( el ) => this.mapSearch = el}

            uid={uid}

            dataDataContext={dataDataContext}
            dataContext={dataContext}
            rootItemId={viewItem?.id}
            rootItemFormState={rootItemFormState}

            onSelectElement={this.onSelectElement}
            selectedElementId={selectedElementId}
        />;
    }

    getMapSearchEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getForm( uid : string, dataContext : string, formProps : any, additionalElusiveDataContexts : string[], viewKey : string, rootItemId : number, readOnly : boolean, treeVariables? : Variables ) {
        return <Form
            key={uid}

            {...formProps}

            uid={uid}

            dataContext={dataContext}
            additionalElusiveDataContexts={additionalElusiveDataContexts}
            schema="CFG"
            viewKey={viewKey}
            mode="embedded"
            readOnly={readOnly}
            rootItemId={rootItemId}
            treeVariables={treeVariables}
        />
    }

    getFormEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    /**
     * React render method
     * @return {*}
     */
    render() {
        const {
                  dataContext,
                  elusiveData,
                  menu2Width,
                  menuWidth,
                  rootItemId,
                  rootItemKey,
                  selectedElementId,
                  status,
                  subPanelProps,
                  timestamp,
                  timezone,
                  uid,
                  user
              }                     = this.props as AllProps,
              {
                  initialized,
                  errorCount,
                  errorTooltip,
                  errorMessage,
                  rootItemFormState,
                  showCreateNewMapSearchDialog
              }                     = this.state,
              dataDataContext       = this.getDataDataContext( dataContext ),
              itemId                = rootItemId
                  ?? dataUtils.findItem( dataContext, 'MapSearch', rootItemKey ?? '' )?.id
                  ?? 0,
              rootItem              = global.data[ dataContext ]?.items[ itemId ],
              readOnly              = !( user?.isMemberOfGroup( config.sysGroups?.admin ?? '' ) || !rootItem?.properties.get( metaModel[ 'CFG' ].getPropertyType( 'SystemRelevant' )?.id ?? 0 )?.value ),
              dataStructure         = this.tree?.dataStructure,
              dataObjectItems       = dataStructure
                  ? extractDataObjectsFromDataStructure( dataStructure ).map( dO => global.data[ dataContext ]?.items?.[ dO.origId ] )
                  : [],
              mapDataSetColors      = this.mapSearch?.markers?.reduce(
                  ( acc, markerGroup ) => {
                      acc[ markerGroup.dataSetId ] = markerGroup.color;
                      return acc;
                  },
                  {} as { [ key : string ] : string }
              ),
              selectedLayoutElement = selectedElementId
                  ? global.data[ dataContext ]?.items[ selectedElementId ]
                  : undefined,
              currentItemId         = selectedLayoutElement?.id
                  ?? rootItem?.id,
              currentItem           = selectedLayoutElement
                  ?? rootItem,
              panelConfigs          = user && currentItem
                  ? user.getPanelPropsForItemType( 'CFG', currentItem.itemTypeId, undefined, 'Form' ) as FormPanelProps[]
                  : undefined,
              panelConfig           = panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^MSSp_/ ) )
                  ?? panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^XdSp_/ ) )
                  ?? panelConfigs?.[ 0 ] ?? null,
              searchTreeUid         = `tree-${rootItem?.id}`,
              fullSearchTreeUid     = `${uid}_${searchTreeUid}`,
              formUid               = `form-${currentItemId}`,
              fullFormUid           = `${uid}_${formUid}`,
              mapUid                = `map-${rootItem?.id}`,
              fullMapUid            = `${uid}_${mapUid}`,
              attributesUid         = `attributes-${currentItemId}`,
              fullAttributesUid     = `${uid}_${attributesUid}`;

        return <>
            {showCreateNewMapSearchDialog &&
                this.getCreateNewMapSearchDialog()
            }
            {elusiveData?.showCouldNotSavePopup &&
                <Dialog
                    key={`${uid}_dialog`}
                    header={__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.title' )}
                    footer={<div><Button label="OK" className="p-button-outlined"
                                         onClick={() => this.props.setElusiveData( uid, { showCouldNotSavePopup: false } )}/></div>}
                    maskClassName="yn-dialog-panel-modal"
                    appendTo="self"
                    position="top"
                    visible={true}
                    style={{ width: 'auto', maxWidth: '80%' }}
                    closable={false}
                    className="yn-error-dialog"
                    onHide={() => {
                    }}>
                    {errorMessage}
                </Dialog>
            }
            {this.getHeaderbar(
                uid,
                dataContext,
                rootItem
            )}
            {this.getToolbar(
                uid,
                dataContext,
                rootItem,
                readOnly,
                errorCount,
                errorTooltip
            )}
            <LoadingMaskPanel forComponent={uid} className="yn-flex-layout-parent">
                <Toast
                    ref={( el ) => this.toast = el}

                    key={`${uid}_toast`}

                    position="bottom-right"
                    style={{ position: 'absolute', marginTop: '0' }}
                />
                <ResizeableLeftMenuPanel
                    key={`${uid}_menuContentPanel`}

                    uid={uid}

                    menuWidth={menuWidth}
                    minMenuWidth={15}
                    maxMenuWidth={25}
                    contentStyle={this.menuContentPanelContentStyle}
                    onMenuScroll={this.onMenuScroll}
                    onMenuWidthChange={this.onMenuWidthChange}

                    menu={initialized && !showCreateNewMapSearchDialog
                        ? this.getSearchTree(
                            fullSearchTreeUid,
                            dataContext,
                            subPanelProps?.[ searchTreeUid ],
                            rootItem,
                            readOnly,
                            [ uid + '_DataObjects', fullFormUid, fullMapUid, uid ],
                            selectedElementId
                        )
                        : <></>
                    }
                    content={<div className="yn-flex-layout-parent" style={{ width: '100%' }}>
                        <ResizeableRightMenuPanel
                            key={`${uid}_menu2ContentPanel`}

                            uid={uid}

                            menuWidth={menu2Width}
                            minMenuWidth={15}
                            maxMenuWidth={33}
                            menuClassName="yn-content-panel-container yn-menu-form yn-formdesigner-properties-panel"
                            onMenuWidthChange={this.onMenu2WidthChange}

                            content={<LoadingMaskPanel forComponent={fullMapUid}
                                                       className="yn-flex-layout-parent yn-content-panel-container p-0"
                                                       style={{ position: 'absolute', width: '100% !important', height: 'calc(100% + .5rem) !important' }}>
                                {initialized && !showCreateNewMapSearchDialog
                                    ? this.getMapSearch(
                                        fullMapUid,
                                        dataContext,
                                        dataDataContext,
                                        rootItemFormState,
                                        rootItem,
                                        selectedElementId ?? currentItemId
                                    )
                                    : undefined
                                }
                            </LoadingMaskPanel>}
                            menu={initialized && !showCreateNewMapSearchDialog
                                ? <LoadingMaskPanel key={`${uid}_map_loadingmask`}
                                                    forComponent={panelConfig?.panelType === 'Form' ? fullFormUid : fullAttributesUid}
                                                    className="yn-flex-layout-parent">
                                    <div>
                                        {panelConfig?.panelType === 'Form' && currentItemId
                                            ? this.getForm(
                                                fullFormUid,
                                                dataContext,
                                                subPanelProps?.[ formUid ],
                                                [ uid + '_DataObjects', fullSearchTreeUid, fullMapUid, uid ],
                                                panelConfig.viewKey,
                                                currentItemId,
                                                readOnly,
                                                {
                                                    dataObjectItems,
                                                    mapDataSetColors
                                                }
                                            )
                                            : undefined
                                        }
                                        {panelConfig?.panelType !== 'Form' && currentItem &&
                                            <div key={`${uid}_formview`}>
                                                <AttributesForm
                                                    key={fullAttributesUid}

                                                    {...subPanelProps?.[ attributesUid ]}

                                                    uid={fullAttributesUid}
                                                    dataContext={dataContext}
                                                    style={{ padding: 0 }}
                                                    schema="CFG"
                                                    status={status}
                                                    timestamp={timestamp}
                                                    timezone={timezone}
                                                    item={currentItem}
                                                    editable
                                                />
                                            </div>
                                        }
                                    </div>
                                </LoadingMaskPanel>
                                : <></>
                            }
                        />
                    </div>}
                />
            </LoadingMaskPanel>
        </>;
    }
}


// redux stuff

/**
 *
 * @param state
 * @param ownProps
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        allElusiveData  : undefined,
        elusiveData     : undefined,
        intercomMessages: undefined,
        user            : undefined
    };

    retVal.allElusiveData = state.elusiveData;

    if ( ownProps.uid ) {

        // get elusiveData
        if ( state.elusiveData?.[ ownProps.uid ] ) {
            retVal.elusiveData = state.elusiveData[ ownProps.uid ];
        }

        // get intercom messages
        if ( state.intercom?.[ ownProps.uid ] ) {
            retVal.intercomMessages = state.intercom[ ownProps.uid ];
        }

        // get the running xhr request belonging to this component (uid)
        if ( state.xhr?.[ ownProps.uid ] ) {
            retVal.xhrRequestsForComponent = state.xhr[ ownProps.uid ];
        }
    }

    // get session user
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * Bind action creators to component props
 * @param dispatch
 * @return {*}
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( MapSearchView );
